import { useCallback, useRef, useState, type ReactNode } from 'react';

import { ApiError } from '@/api/http-client';
import { SenhaDoCertificadoDialogo } from '@/features/documentos/components/SenhaDoCertificadoDialogo';

/** Códigos com que o servidor pede (ou recusa) a senha do certificado A1. */
const PEDE_SENHA = 'SENHA_DO_CERTIFICADO_NECESSARIA';
const SENHA_ERRADA = 'SENHA_DO_CERTIFICADO_INCORRETA';

interface Pedido {
  erro: string | null;
  tentativa: number;
  resolver: (senha: string | null) => void;
}

/**
 * Assinatura com certificado A1 que pede a senha SÓ quando o servidor exige.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  A tela não decide se precisa de senha — quem sabe é o servidor: o médico
 *  pode ter guardado a senha, pode não ter certificado, ou o documento pode
 *  ser de outro veterinário. Então a primeira tentativa vai sem senha, e só
 *  `SENHA_DO_CERTIFICADO_NECESSARIA`/`_INCORRETA` abrem o diálogo. Senha
 *  errada tenta de novo com a mensagem no campo; qualquer outro erro sobe para
 *  a tela tratar como sempre tratou.
 *
 *  O servidor responde 422 nesses casos — nunca 401, que o `httpClient`
 *  interpreta como sessão perdida.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Uso: `await comSenhaSeNecessario((senha) => assinar.mutateAsync({ id, data: ... }))`.
 * Devolve `null` quando a pessoa cancela o diálogo — não é erro, não merece toast.
 */
export function useSenhaDoCertificado(): {
  comSenhaSeNecessario: <T>(assinar: (senhaCertificado?: string) => Promise<T>) => Promise<T | null>;
  dialogo: ReactNode;
} {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const tentativas = useRef(0);

  const pedirSenha = useCallback(
    (erro: string | null) =>
      new Promise<string | null>((resolver) => {
        tentativas.current += 1;
        setPedido({ erro, tentativa: tentativas.current, resolver });
      }),
    [],
  );

  const comSenhaSeNecessario = useCallback(
    async <T,>(assinar: (senhaCertificado?: string) => Promise<T>): Promise<T | null> => {
      let erro: string | null;

      try {
        return await assinar();
      } catch (e) {
        if (!(e instanceof ApiError) || (e.codigo !== PEDE_SENHA && e.codigo !== SENHA_ERRADA)) throw e;
        // A senha GUARDADA pode ter deixado de funcionar: aí o primeiro pedido
        // já chega com a mensagem de erro.
        erro = e.codigo === SENHA_ERRADA ? e.message : null;
      }

      try {
        for (;;) {
          const senha = await pedirSenha(erro);
          if (senha === null) return null;

          try {
            return await assinar(senha);
          } catch (e) {
            if (!(e instanceof ApiError) || e.codigo !== SENHA_ERRADA) throw e;
            erro = e.message;
          }
        }
      } finally {
        setPedido(null);
      }
    },
    [pedirSenha],
  );

  const dialogo = (
    <SenhaDoCertificadoDialogo
      aberto={pedido !== null}
      erro={pedido?.erro ?? null}
      tentativa={pedido?.tentativa ?? 0}
      aoResponder={(senha) => pedido?.resolver(senha)}
    />
  );

  return { comSenhaSeNecessario, dialogo };
}
